const React = require('react');
const T = require('prop-types');
const { default: Classes } = require('./styles.scss');
const IsEmail = require('utils/is-email');

const PrefsDomain = require('domain/preferences');
const PreventNavigationDialog = require('components/PreventNavigationDialog');
const useForm = require('hooks/useForm');
const UserPreferencesFormConfig = require('./formConfig');

const {
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} = React;

const internals = {};

internals.SPECIAL_TEXT_ALERT_FIELDS = ['sms', 'phone', 'phoneCountry'];

internals.getRegularPreferenceFields = () => PrefsDomain.prefFields
    .concat(PrefsDomain.profileFields)
    .filter((field) => !internals.SPECIAL_TEXT_ALERT_FIELDS.includes(field));

internals.buildUserPreferences = ({
    fields,
    values
}) => {

    return fields.reduce((collector, field) => {

        let fieldValue = values[field];

        if (field === 'profileEmail') {
            fieldValue = (fieldValue || '').replace(/\s/g, '');
        }

        collector[field] = fieldValue;

        return collector;
    }, {});
};


internals.UserPreferences = function UserPreferences(props, ref) {

    const {
        history,
        onSubmit,
        onSubmitNoRedirect,
        onInvalidSubmit,
        onChange,
        preferences,
        preferencesSubmiting,
        isSignupPage,
        isEditPage,
        hideJobAlerts,
        headersToShow,
        fieldsToShow,
        useWrapper
    } = props;

    const methodsRef = useRef(null);
    const fields = useMemo(() => internals.getRegularPreferenceFields(), []);
    const [isSaving, setIsSaving] = useState(false);

    const validate = useCallback((field, values) => {

        const formValues = values || methodsRef.current?.getValues?.() || {};
        const value = formValues[field];

        if (field === 'profileEmail') {
            if (value && !IsEmail(value)) {
                return 'Invalid email address';
            }
        }

        return null;
    }, []);

    const getUserPreferences = useCallback((values) => {

        const formValues = values || methodsRef.current?.getValues?.() || {};
        return internals.buildUserPreferences({
            fields,
            values: formValues
        });
    }, [fields]);

    const notifyChange = useCallback((values) => {

        if (typeof onChange !== 'function') {
            return;
        }

        Object.entries(values).forEach(([key, value]) => {

            onChange(key, value);
        });
    }, [onChange]);

    const hasErrors = useCallback(() => {

        const methods = methodsRef.current;
        const fieldValidationHasErrors = fields.some((field) => !!validate(field));

        return fieldValidationHasErrors || !!Object.keys(methods?.formState?.errors || {}).length;
    }, [fields, validate]);

    const handleInvalidSubmit = useCallback(() => {

        if (typeof onInvalidSubmit === 'function') {
            return onInvalidSubmit();
        }

        return null;
    }, [onInvalidSubmit]);

    const performSubmit = useCallback(async (submitFunction, values, cb) => {

        const methods = methodsRef.current;

        if (!methods || typeof submitFunction !== 'function') {
            return null;
        }

        const validationErrors = fields.reduce((collector, field) => {

            const message = validate(field, values);

            if (message) {
                collector[field] = message;
            }

            return collector;
        }, {});

        if (Object.keys(validationErrors).length) {
            Object.entries(validationErrors).forEach(([field, message]) => {

                methods.setError(field, {
                    type: 'manual',
                    message
                });
            });

            return handleInvalidSubmit();
        }

        const userPreferences = getUserPreferences(values);

        try {
            setIsSaving(true);
            await submitFunction(userPreferences, cb || (() => null));

            methods.reset({
                ...methods.getValues(),
                ...userPreferences
            });

            setIsSaving(false);

            return null;
        }
        catch (err) {
            setIsSaving(false);
            return null;
        }
    }, [fields, getUserPreferences, handleInvalidSubmit, validate]);

    const submitWith = useCallback((submitFunction, cb) => {

        const methods = methodsRef.current;

        if (!methods) {
            return null;
        }

        return methods.handleSubmit(
            (values) => performSubmit(submitFunction, values, cb),
            handleInvalidSubmit
        )();
    }, [handleInvalidSubmit, performSubmit]);

    const submit = useCallback((cb) => submitWith(onSubmit, cb), [onSubmit, submitWith]);
    const submitNoRedirect = useCallback((cb) => submitWith(onSubmitNoRedirect, cb), [onSubmitNoRedirect, submitWith]);

    const formConfig = UserPreferencesFormConfig.buildFormConfig({
        fieldsToShow,
        headersToShow,
        hideJobAlerts,
        isEditPage,
        isSignupPage,
        methodsRef,
        onSubmit: (values) => performSubmit(onSubmit, values),
        noDivider: headersToShow?.length === 1,
        notifyChange,
        prefs: preferences,
        validate
    });

    const {
        methods,
        Form
    } = useForm(formConfig);

    methodsRef.current = methods;

    useImperativeHandle(ref, () => ({
        fields,
        validate,
        submit,
        submitNoRedirect,
        getUserPreferences,
        hasErrors
    }), [
        fields,
        getUserPreferences,
        hasErrors,
        submit,
        submitNoRedirect,
        validate
    ]);

    const pageHasErrors = hasErrors();
    const pageIsDirty = methods.formState.isDirty;
    const shouldShowPreventNavigationDialog = pageIsDirty
        && !isSaving
        && !preferencesSubmiting
        && !isSignupPage
        && !isEditPage;

    return (
        <div className={useWrapper ? Classes.wrapper : ''}>
            <PreventNavigationDialog
                saveFunction={submitNoRedirect}
                pageHasErrors={pageHasErrors}
                alertMsg={'You have made changes.\n Do you want to save or discard them?'}
                when={shouldShowPreventNavigationDialog}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => shouldShowPreventNavigationDialog}
            />
            {Form}
        </div>
    );
};

const UserPreferences = React.forwardRef(internals.UserPreferences);

UserPreferences.propTypes = {
    history: T.object,
    onSubmit: T.func,
    onSubmitNoRedirect: T.func,
    onInvalidSubmit: T.func,
    onChange: T.func,
    preferences: T.object,
    preferencesSubmiting: T.bool,
    isSignupPage: T.bool,
    isEditPage: T.bool,
    hideJobAlerts: T.bool,
    headersToShow: T.arrayOf(T.oneOf([
        'communications',
        'connections',
        'groups',
        'emails',
        'offers',
        'other'
    ])),
    fieldsToShow: T.arrayOf(T.oneOf([
        'profileEmail',
        'connectionsVisibility',
        'groupVisibility',
        'groupMsgNotifications',
        'digestInterval',
        'pendingConnections',
        'careerAlerts',
        'offerAlerts',
        'messageNotifications'
    ])),
    useWrapper: T.bool
};

UserPreferences.defaultProps = {
    headersToShow: [
        'communications',
        'connections',
        'groups',
        'emails',
        'offers',
        'other'
    ],
    fieldsToShow: [
        'profileEmail',
        'connectionsVisibility',
        'groupVisibility',
        'groupMsgNotifications',
        'digestInterval',
        'pendingConnections',
        'careerAlerts',
        'offerAlerts',
        'messageNotifications'
    ],
    useWrapper: true
};

module.exports = UserPreferences;
module.exports.internals = internals;
